/**
 * TutorialPlayer — the middle of the Learn page: one tutorial, in full.
 *
 * The film plays in a clean frame with its captions UNDER it, not over it —
 * a caption laid over the picture covers the very control it is talking
 * about (the typing bar, more often than not). Beneath the captions: the
 * film's beats as a clickable transcript, the written steps, and a button
 * to the place the tutorial is about.
 *
 * The films are silent by contract; the captions are the narration, and
 * they are i18n keys owned by journeyConfig (one timing per beat), so the
 * same film narrates itself in either language.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { FILM_CAPTIONS, clipFile, type JourneyClipId } from '../config/journeyConfig';
import type { LearnTopic } from '../content/catalog';
import { FilmTitleCard } from './FilmTitleCard';
import { FullscreenGlyph } from './FullscreenGlyph';
import { useFullscreen } from '../hooks/useFullscreen';

const fmt = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

interface Props {
  topic: LearnTopic | null;
  lang: 'en' | 'he';
  /** Bumped by the page when the film should start on its own (the Guide
   *  opened it, or a row with a film was chosen). */
  playKey: number;
  /** The Guide's one-line reason for opening this, when it did. */
  why: string | null;
  onGoTo: (topic: LearnTopic) => void;
}

export function TutorialPlayer({ topic, lang, playKey, why, onGoTo }: Props) {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [timeMs, setTimeMs] = useState(0);
  const [durMs, setDurMs] = useState(0);
  const [missing, setMissing] = useState(false);
  // Autoplay is a request the video may not be able to honor yet (not
  // loaded). Park it here and act on it the moment it can play.
  const wantPlay = useRef(false);
  /** Whether this film has been started yet — the title card plays once. */
  const playedRef = useRef(false);

  const film = topic?.film ?? null;
  const beats = useMemo(() => (film ? FILM_CAPTIONS[film] ?? [] : []), [film]);
  // The opening title card: shown from the first play until it has had its
  // moment, then it fades and the film runs underneath it.
  const [titleUp, setTitleUp] = useState(false);
  // Full screen takes the whole film block — picture, bar and caption — so
  // the narration comes along.
  const filmRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, toggleFullscreen] = useFullscreen(filmRef);

  useEffect(() => {
    setReady(false);
    setPlaying(false);
    setEnded(false);
    setTimeMs(0);
    setDurMs(0);
    setMissing(false);
    setTitleUp(false);
    playedRef.current = false;
  }, [film]);

  /** Resume where we are, with no title card. */
  const resume = useCallback(() => {
    void videoRef.current?.play().catch(() => undefined);
  }, []);

  const play = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.ended) v.currentTime = 0;
    // The title card belongs to STARTING the film, not to being near its
    // start. Keyed on "has this film played at all yet", because a tolerance
    // on currentTime also caught a deliberate rewind: scrubbing back to 0.2s
    // or clicking an early transcript beat replayed the whole intro instead
    // of resuming.
    if (!playedRef.current) {
      playedRef.current = true;
      setTitleUp(true);
      return;
    }
    resume();
  }, [resume]);

  const onTitleDone = useCallback(() => {
    setTitleUp(false);
    void videoRef.current?.play().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (playKey === 0 || !film) return;
    wantPlay.current = true;
    if (ready) {
      wantPlay.current = false;
      play();
    }
    // `ready` is deliberately not a dependency: this effect is about a NEW
    // request, and onCanPlay handles the not-yet-ready case below.
  }, [playKey, film]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCanPlay = () => {
    setReady(true);
    if (wantPlay.current) {
      wantPlay.current = false;
      play();
    }
  };

  // One scan, not two: the caption IS the beat the film has reached, and a
  // film always opens on its first line rather than on an empty band.
  const beatIndex = useMemo(() => {
    let i = -1;
    beats.forEach((b, n) => { if (timeMs >= b.atMs) i = n; });
    return i;
  }, [beats, timeMs]);
  const caption = useMemo(() => {
    const beat = beats[beatIndex >= 0 ? beatIndex : 0];
    return beat ? t(beat.key) : null;
  }, [beats, beatIndex, t]);

  const seek = (ms: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = ms / 1000;
    setTimeMs(ms);
    // Resume, never `play()`: a seek is a request for THIS position, so it
    // must not be answered with the opening title card.
    if (v.paused) {
      playedRef.current = true;
      resume();
    }
  };

  if (!topic) {
    return (
      <div className="lp-player lp-player-empty" data-testid="tutorial-player">
        <p>{t('learn.pickOne')}</p>
      </div>
    );
  }

  const hasFilm = Boolean(film) && !missing;

  return (
    <div className="lp-player" data-testid="tutorial-player" data-topic={topic.id}>
      <header className="lp-player-head">
        <h1 className="lp-player-title" dir="auto">{topic.title[lang]}</h1>
        <p className="lp-player-summary" dir="auto">{topic.summary[lang]}</p>
        {why && (
          <p className="lp-player-why" dir="auto" data-testid="tutorial-why">
            <span className="lp-player-why-mark" aria-hidden>?</span> {why}
          </p>
        )}
      </header>

      {hasFilm ? (
        <div className={`lp-film ${isFullscreen ? 'is-fullscreen' : ''}`} ref={filmRef}>
          <div className={`lp-screen ${ready ? 'is-ready' : ''}`}>
            <video
              ref={videoRef}
              src={`/clips/journey/${clipFile(film as JourneyClipId)}`}
              muted
              playsInline
              /* metadata, not auto: the Learn page selects a filmed topic on
                 arrival without playing it, and `auto` fetched the whole
                 1–4 MB clip on every visit. The wantPlay/onCanPlay handshake
                 below already covers "asked to play before it was ready". */
              preload="metadata"
              onCanPlay={onCanPlay}
              onError={() => setMissing(true)}
              onLoadedMetadata={(e) => setDurMs(e.currentTarget.duration * 1000)}
              onTimeUpdate={(e) => setTimeMs(e.currentTarget.currentTime * 1000)}
              onPlay={() => { setPlaying(true); setEnded(false); }}
              onPause={() => setPlaying(false)}
              onEnded={() => { setPlaying(false); setEnded(true); }}
              onClick={() => (playing ? videoRef.current?.pause() : play())}
              data-testid="tutorial-video"
            />
            {titleUp && <FilmTitleCard title={topic.title[lang]} onDone={onTitleDone} />}
            {!playing && ready && !titleUp && (
              <button className="lp-screen-play" onClick={play} aria-label={t(ended ? 'learn.replay' : 'learn.play')} data-testid="tutorial-play">
                {ended ? (
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 3v6h6" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
                )}
              </button>
            )}
          </div>

          <div className="lp-bar">
            <button
              className="lp-bar-play"
              onClick={() => (playing ? videoRef.current?.pause() : play())}
              aria-label={t(playing ? 'learn.pause' : 'learn.play')}
            >
              {playing ? (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden><path d="M8 5v14l11-7z" /></svg>
              )}
            </button>
            <input
              className="lp-bar-scrub"
              type="range"
              min={0}
              max={Math.max(1, Math.floor(durMs))}
              value={Math.floor(timeMs)}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label={t('learn.scrub')}
              style={{ '--lp-progress': `${durMs ? (timeMs / durMs) * 100 : 0}%` } as React.CSSProperties}
            />
            <span className="lp-bar-time" dir="ltr">{fmt(timeMs)} / {fmt(durMs)}</span>
            <button
              className="lp-bar-fullscreen"
              onClick={toggleFullscreen}
              aria-label={t(isFullscreen ? 'learn.exitFullscreen' : 'learn.fullscreen')}
              title={t(isFullscreen ? 'learn.exitFullscreen' : 'learn.fullscreen')}
              data-testid="tutorial-fullscreen"
            >
              <FullscreenGlyph exit={isFullscreen} />
            </button>
          </div>

          {/* The narration, outside the picture: a fixed-height band, text
              centred, the beat dots centred beneath it. */}
          <div className="lp-caption" data-testid="tutorial-caption">
            <span className="lp-caption-text" dir="auto">{caption ?? ''}</span>
            {beats.length > 1 && (
              <span className="lp-caption-dots" aria-hidden>
                {beats.map((b, i) => (
                  <i key={b.key} className={i <= beatIndex ? 'on' : ''} />
                ))}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="lp-nofilm">{t('learn.noFilm')}</p>
      )}

      {beats.length > 0 && hasFilm && (
        <section className="lp-section">
          <h3>{t('learn.inThisFilm')}</h3>
          <ol className="lp-beats">
            {beats.map((b, i) => (
              <li key={b.key} className={i === beatIndex ? 'is-now' : ''}>
                <button onClick={() => seek(b.atMs)}>
                  <span className="lp-beat-time">{fmt(b.atMs)}</span>
                  <span className="lp-beat-text" dir="auto">{t(b.key)}</span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {topic.steps && topic.steps.length > 0 && (
        <section className="lp-section">
          <h3>{t('learn.howToDoIt')}</h3>
          <ol className="lp-steps">
            {topic.steps.map((s, i) => (
              <li key={i} dir="auto">{s[lang]}</li>
            ))}
          </ol>
        </section>
      )}

      {topic.route !== undefined && (
        <button className="lp-goto" onClick={() => onGoTo(topic)} data-testid="learn-goto">
          {t('learn.takeMeThere')}
          <span className="lp-goto-arrow" aria-hidden>→</span>
        </button>
      )}
    </div>
  );
}
